/**
 * Every string the portal shows, in one place.
 *
 * Two rules govern this file.
 *
 * Neutrality: an unauthorised address and an authorised one must produce the
 * same words. That is why there is one failure message for the whole verify
 * step, covering a wrong code, an expired code, a code already used and an
 * address that was never authorised. Wording that distinguishes them is an
 * enumeration oracle.
 *
 * Absence: nothing in this file, and nothing in this application's chrome,
 * makes a compliance claim. The sentence that used to do so was removed from
 * the contract deliberately, with nothing in its place. That content belongs
 * in the documents the portal serves. The copy lint enforces the absence.
 */

import type { CredentialKind } from '@insuredhq/contract';

/** Shown after the email step, whatever the API answered. */
export { AUTH_GENERIC_RESPONSE_MESSAGE } from '@insuredhq/contract';

/** The one and only outcome of a failed verification, whatever went wrong. */
export const VERIFY_FAILED_MESSAGE =
  'That code could not be verified. Request a new code and try again.';

/** Shown when a session ends, whether it expired or access was withdrawn. */
export const SESSION_ENDED_MESSAGE =
  'Your session has ended. Enter your email address to continue.';

/** Shown when something fails in a way we are not going to explain. */
export const GENERIC_FAILURE_MESSAGE =
  'That could not be completed. Please try again in a moment.';

/** Shown when the address bar holds no portal slug. */
export const NO_SLUG_MESSAGE =
  'This link is not complete. Use the full address from your invitation email.';

/** Shown against a request item answered in prose outside this portal. */
export const RESPONSE_PROVIDED_SEPARATELY = 'Answered in the response below.';

export const PORTAL_LOAD_FAILURE_MESSAGE =
  'We could not load your documents. Try again in a moment.';
export const PORTAL_LOAD_RETRY = 'Try again';

export const PORTAL_TITLE = 'InsuredHQ Trust Center';

/**
 * Shown beside the logo image in the masthead. The image is the InsuredHQ
 * wordmark, so the text carries only the part the image does not say.
 */
export const PORTAL_NAME = 'Trust Center';
export const PORTAL_WORDMARK_ALT = 'InsuredHQ';

/**
 * Shown when the Turnstile widget fails to load or render, instead of a
 * silently disabled form. A prospect who cannot sign in must at least see that
 * the page knows it is broken. The message names no cause on purpose: the
 * cause is a config or network matter and none of the visitor's business.
 */
export const TURNSTILE_FAILED_MESSAGE =
  'The verification step could not load. Refresh the page to try again. If it keeps happening, contact your InsuredHQ representative.';

export const GATE_HEADING = 'Verify your email address';
export const GATE_INTRO =
  'This portal is restricted to named reviewers. Enter your work email address and we will send you a single use code.';
export const GATE_EMAIL_LABEL = 'Work email address';
export const GATE_SUBMIT = 'Send code';

export const CODE_HEADING = 'Enter your code';
export const CODE_LABEL = 'Access code';
export const CODE_SUBMIT = 'Verify';
export const CODE_BACK = 'Use a different address';

export const REQUEST_HEADING = 'You requested the following';
export const LIBRARY_HEADING = 'Document library';
export const TIER_1_HEADING = 'Available on email verification';
export const TIER_2_HEADING = 'Available under NDA';

/**
 * The boundary between the tiers. Chip on the rule, sentence beneath it. The
 * chip's case lives in the string because the copy lint reads the stylesheet
 * and objects to the CSS property that would set it there.
 */
export const NDA_BOUNDARY_CHIP = 'UNDER NDA';
export const NDA_BOUNDARY_NOTE =
  'Opening any document below requires accepting a short confidentiality agreement.';
export const TIER_2_EMPTY = 'No documents in this group.';
export const TIER_1_EMPTY = 'No documents in this group.';

export const LOCKED_LABEL = 'Accept NDA to open';
export const OPEN_LABEL = 'Open';
export const OPENING_LABEL = 'Opening';

export const WATERMARK_NOTICE =
  'Your copy is stamped with your email address and the time you opened it.';

export const NDA_HEADING = 'Confidentiality undertaking';
export const NDA_ACCEPT = 'I accept';
export const NDA_CANCEL = 'Cancel';
export const NDA_RECORD_NOTICE =
  'Your acceptance is recorded with your email address, the time and your IP address.';

export const SIGN_OUT_NOTE =
  'Close this tab when you are finished. Every document you open is logged.';

/* ---- Sandbox access ---------------------------------------------------- */

export const ACCESS_HEADING = 'Sandbox access';
export const ACCESS_LEDE =
  'Links and test credentials for the InsuredHQ sandbox. Each secret can be shown once.';
export const ACCESS_LINKS_HEADING = 'Links';
export const ACCESS_CREDENTIALS_HEADING = 'Credentials';

/** Chip text. Upper case lives here for the same reason as NDA_BOUNDARY_CHIP. */
export const CREDENTIAL_KIND_LABELS: Record<CredentialKind, string> = {
  api: 'API',
  login: 'LOGIN',
};

/** A fixed row, not derived from the secret, so it says nothing about its length. */
export const CREDENTIAL_MASK = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';
export const CREDENTIAL_MASK_LABEL = 'Hidden';

export const REVEAL_LABEL = 'Reveal once';
export const REVEALING_LABEL = 'Revealing';
export const REVEAL_CONFIRM_MESSAGE =
  'This can only be shown once. Copy it now and store it somewhere safe.';
export const REVEAL_CONFIRM = 'Confirm';
export const REVEAL_CANCEL = 'Cancel';
export const REVEALED_NOTE = 'It will not be shown again after you leave this page.';
export const COPY_LABEL = 'Copy';
export const COPIED_LABEL = 'Copied';
/** Accessible names for a Copy button, which sits on every detail row. */
export const copyLabelFor = (label: string) => `Copy ${label}`;
export const copiedLabelFor = (label: string) => `${label} copied`;

export const CREDENTIAL_UNAVAILABLE =
  'This is no longer available. Ask your InsuredHQ contact to reissue it.';
export const CREDENTIAL_RETRIEVED =
  'Already retrieved. Ask your InsuredHQ contact to reissue it if you need it again.';

export const credentialAvailableUntil = (date: string) => `Available until ${date}.`;
export const credentialRetrievedOn = (date: string) =>
  `Retrieved on ${date}. Ask your InsuredHQ contact to reissue it if you need it again.`;
